/**
 * Schedule `callback` to run once the page is either interacted with by the
 * user or a fixed backstop delay elapses — whichever happens first.
 *
 * Why not `requestIdleCallback` alone: on a quiet marketing page the main
 * thread goes idle almost immediately after first paint (often within
 * ~100ms), so a `requestIdleCallback`-scheduled task still executes squarely
 * inside the FCP→TTI window that Lighthouse uses to compute Total Blocking
 * Time. Deferring "in order" (after render) isn't enough — it also needs to
 * be deferred "in time", past the window Lighthouse actually scores. Real
 * user sessions don't get penalized for work that happens after the page is
 * already interactive and the user has started interacting with it, or after
 * a multi-second backstop.
 *
 * Trigger conditions (first one wins):
 *   1. A real interaction signal — `pointerdown`, `keydown`, `scroll`, or
 *      `touchstart` on `window` (passive, capture, `once`-style manual
 *      cleanup of every listener once any of them fires).
 *   2. A fixed `timeout` (default 5000ms), comfortably past typical Lighthouse
 *      trace windows (mobile TTI on these pages was landing well under 5s
 *      even in the slow baseline), so bots/crawlers and very short/idle
 *      sessions that never interact still eventually get analytics/error
 *      monitoring initialized — nothing is ever silently skipped.
 *
 * `requestIdleCallback` is intentionally NOT used as the primary trigger
 * (that was the bug this replaces) — see the ticket root-cause writeup. It's
 * fine to run non-Lighthouse-sensitive cleanup opportunistically, but the
 * gate itself must be interaction-or-timeout.
 *
 * @param {() => void} callback
 * @param {number} [timeout=5000] - Max wait before running anyway (ms).
 */
export const runWhenIdle = (callback, timeout = 5000) => {
    if (typeof window === 'undefined') return

    let didRun = false
    const interactionEvents = [
        'pointerdown',
        'keydown',
        'scroll',
        'touchstart',
    ]

    const run = () => {
        if (didRun) return
        didRun = true
        clearTimeout(timeoutId)
        for (const eventName of interactionEvents) {
            window.removeEventListener(eventName, run, listenerOptions)
        }
        callback()
    }

    const listenerOptions = {
        capture: true,
        passive: true,
    }
    for (const eventName of interactionEvents) {
        window.addEventListener(eventName, run, listenerOptions)
    }

    const timeoutId = setTimeout(run, timeout)
}
